<template>
  <div>
    <label class="d-block mb-2">Zdjęcia</label>
    
    <ImageDropZone @files-selected="handleFilesSelected" />
    
    <ImagePreviewGrid
      :items="items"
      :dragging-index="draggingIndex"
      :drag-over-index="dragOverIndex"
      @drag-start="onDragStart"
      @drag-over="onItemDragOver"
      @drop="onItemDrop"
      @drag-end="onDragEnd"
      @remove="remove"
      @remove-all="removeAll"
    />
  </div>
</template>

<script setup lang="ts">
import ImageDropZone from './ImageDropZone.vue';
import ImagePreviewGrid from './ImagePreviewGrid.vue';
import { useApiConfig } from '../composables/useApi';
import { useImageQueue } from '../modules/offers/composables/useImageQueue';
import { useReorder } from '../modules/offers/composables/useReorder';
import { imagesApi } from '../modules/offers/services/images.api';

const props = defineProps<{
  offerId?: number | null;
}>();

const api = useApiConfig();
const {
  items,
  reset,
  setExistingImages,
  addFiles,
  moveItem,
  uploadAll: queueUploadAll,
} = useImageQueue();
const {
  draggingIndex,
  dragOverIndex,
  onDragStart,
  onItemDragOver,
  onItemDrop,
  onDragEnd,
} = useReorder((from, to) => moveItem(from, to));

function handleFilesSelected(files: File[]) {
  addFiles(files);
}

// Reorder handled by useReorder

async function uploadAll(offerIdOverride?: number | null) {
  if (!api.apiUploadImage) {
    console.error('apiUploadImage endpoint is empty; cannot upload');
    return;
  }
  const targetOfferId = offerIdOverride ?? props.offerId ?? null;
  if (!targetOfferId) {
    console.error('uploadAll called without a valid offerId');
    return;
  }
  await queueUploadAll(api, targetOfferId);
}

// Persist current order/cover for existing images only
async function saveOrder(offerIdOverride?: number | null) {
  const targetOfferId = offerIdOverride ?? props.offerId ?? null;
  if (!api.apiUpdate || !targetOfferId) return;

  const firstIsNew = !!items.value[0]?.file;
  const images: Array<{ id?: string; filename?: string; url: string; position: number; is_cover: number }> = [];
  items.value.forEach((it, index) => {
    if (it.file) return; // only persist existing ones here
    images.push({
      id: it.id,
      filename: it.filename,
      url: it.previewUrl,
      position: index,
      is_cover: firstIsNew ? 0 : (index === 0 ? 1 : 0),
    });
  });

  if (images.length === 0) return;
  try {
    await imagesApi.persistOrder(api, targetOfferId, images);
  } catch (e) {
    console.error('saveOrder failed', e);
  }
}

function remove(idx: number) {
  const [it] = items.value.splice(idx, 1);
  if (it?.file && it.previewUrl) URL.revokeObjectURL(it.previewUrl);
}

function removeAll() {
  items.value.forEach(item => {
    if (item?.file && item.previewUrl) URL.revokeObjectURL(item.previewUrl);
  });
  items.value.splice(0, items.value.length);
}

defineExpose({ uploadAll, items, reset, setExistingImages, saveOrder });
</script>

